import { useState, useEffect } from "react";
import { Plus, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreateDemoTenantDialog } from "@/components/admin/CreateDemoTenantDialog";
import { TenantStatsCards } from "@/components/admin/TenantStatsCards";
import { TenantListTable } from "@/components/admin/TenantListTable";
import { TenantDeleteDialog } from "@/components/admin/TenantDeleteDialog";
import { TenantInactivateDialog } from "@/components/admin/TenantInactivateDialog";
import { TenantActivateDialog } from "@/components/admin/TenantActivateDialog";
import { TenantDetailsDialog } from "@/components/admin/TenantDetailsDialog";
import { TenantBonusDialog } from "@/components/admin/TenantBonusDialog";
import { useTenants } from "@/hooks/useTenants";

export default function AdminTenantManagement() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tenantToDelete, setTenantToDelete] = useState<string | null>(null);
  const [tenantToInactivate, setTenantToInactivate] = useState<string | null>(null);
  const [tenantToActivate, setTenantToActivate] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
  const [tenantToBonus, setTenantToBonus] = useState<any | null>(null);

  useEffect(() => {
    document.title = 'Dashi | Gerenciamento de Tenants';
  }, []);
  
  const { tenants, isLoading, deleteTenant, inactivateTenant, activateTenant } = useTenants();

  const filteredTenants = tenants?.filter(tenant =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.adminInfo?.admin_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Building2 className="h-8 w-8" />
          Gerenciamento de Tenants
        </h1>
        <p className="text-muted-foreground mt-1">
          Crie e gerencie tenants de demonstração
        </p>
      </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Criar Tenant Demo
        </Button>
      </div>

      <TenantStatsCards tenants={tenants || []} />

      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Input
            placeholder="🔍 Buscar tenant..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <TenantListTable
        tenants={filteredTenants || []}
        isLoading={isLoading}
        onDelete={(id) => setTenantToDelete(id)}
        onInactivate={(id) => setTenantToInactivate(id)}
        onActivate={(id) => setTenantToActivate(id)}
        onViewDetails={(tenant) => setSelectedTenant(tenant)}
        onBonus={(tenant) => setTenantToBonus(tenant)}
      />

      <CreateDemoTenantDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      <TenantDeleteDialog
        open={!!tenantToDelete}
        onOpenChange={(open) => !open && setTenantToDelete(null)}
        onConfirm={() => {
          if (tenantToDelete) {
            deleteTenant.mutate(tenantToDelete);
            setTenantToDelete(null);
          }
        }}
        isDeleting={deleteTenant.isPending}
      />

      <TenantInactivateDialog
        open={!!tenantToInactivate}
        onOpenChange={(open) => !open && setTenantToInactivate(null)}
        onConfirm={() => {
          if (tenantToInactivate) {
            inactivateTenant.mutate(tenantToInactivate);
            setTenantToInactivate(null);
          }
        }}
        isInactivating={inactivateTenant.isPending}
      />

      <TenantActivateDialog
        open={!!tenantToActivate}
        onOpenChange={(open) => !open && setTenantToActivate(null)}
        onConfirm={() => {
          if (tenantToActivate) {
            activateTenant.mutate(tenantToActivate);
            setTenantToActivate(null);
          }
        }}
        isActivating={activateTenant.isPending}
      />

      <TenantDetailsDialog
        tenant={selectedTenant}
        adminInfo={selectedTenant?.adminInfo}
        open={!!selectedTenant}
        onOpenChange={(open) => !open && setSelectedTenant(null)}
      />

      <TenantBonusDialog
        tenant={tenantToBonus}
        open={!!tenantToBonus}
        onOpenChange={(open) => !open && setTenantToBonus(null)}
      />
    </div>
  );
}
